<script setup lang="ts">
    import { computed } from 'vue'
    import { useRoute } from 'vue-router'

    import SchedulerDialog from '@Quest/CorporateScenarios/Components/SchedulerDialog.vue'
    import { SchedulerDialogViewModel } from '@Quest/CorporateScenarios/Components/SchedulerDialog'
    
    const emit = defineEmits<{
        (e: 'scheduled', result: any): void
    }>()
    
    // ---------------- router ----------------

    const route = useRoute()

    // ---------------- route-derived state ----------------

    const workspaceId = computed(() => Number(route.params?.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params?.strategyId ?? ''))
    const scenarioId = computed(() => String(route.params?.scenarioId ?? ''))

    // ---------------- local state ----------------

    // runtime instance → normal import
    const schedulerDialog = new SchedulerDialogViewModel()

    function showSchedulerDialog()
    {
        schedulerDialog.show()
    }


    // ---------------- helpers ----------------

    function getButtonClass(path: string): string
    {
        const routePrefix = `/enterprise-plan/${path}/`
        return route.path.startsWith(routePrefix)
            ? 'btn btn-secondary'
            : 'btn btn-outline-secondary'
    }

    function onScheduled(result: any) 
    {
        emit('scheduled', result)
    }

</script>

<template>
    <div>
        <SchedulerDialog :viewModel="schedulerDialog" @scheduled="onScheduled" />

        <div class="text-right align-self-end">
            <button type="button" title="Open Scheduler (AI Assistant)"
                    class="btn btn-primary mr-1" 
                    style="padding: 0.7em 1em;"
                    @click="showSchedulerDialog">
                <i class="fa-solid fa-robot fa-lg mr-1"></i> Scheduler
            </button>

            <div class="btn-group mr-1" role="group">
                <a title="Portfolios" :class="getButtonClass('portfolios')" style="padding: 0.7em 1em;" v-sanitizedHref="`/#/enterprise-plan/portfolios/${workspaceId}/${strategyId}/${scenarioId}`">
                    <i class="fa-solid fa-sitemap fa-lg"></i>
                </a>
                <a title="Projects" :class="getButtonClass('projects')" style="padding: 0.7em 1em;" v-sanitizedHref="`/#/enterprise-plan/projects/${workspaceId}/${strategyId}/${scenarioId}`">
                    <i class="fa-solid fa-table fa-lg"></i>
                </a>
                <a title="Schedule" :class="getButtonClass('schedule')" style="padding: 0.7em 1em;" v-sanitizedHref="`/#/enterprise-plan/schedule/${workspaceId}/${strategyId}/${scenarioId}`">
                    <i class="fa-regular fa-calendar fa-lg"></i>
                </a>
            </div>

            <a title="Profit & Loss Report" class="mr-1" :class="getButtonClass('pnl')" style="padding: 0.7em 2em;" v-sanitizedHref="`/#/enterprise-plan/pnl/${workspaceId}/${strategyId}/${scenarioId}`">
                P&L
            </a>
            <a title="Enterprise Plan Settings" class="btn btn-outline-secondary mr-1" style="padding: 0.7em 1em;" v-sanitizedHref="`/#/enterprise-plan/settings/${workspaceId}/${strategyId}/${scenarioId}`">
                <i class="fa fa-lg fa-cog" aria-hidden="true"></i>
            </a>
            <a title="Back to Enterprise Plans" class="btn btn-outline-secondary" style="padding: 0.7em 1em;" v-sanitizedHref="`/#/enterprise/planning/${workspaceId}/${strategyId}`">
                <i class="fa-solid fa-door-open fa-lg"></i>
            </a>
        </div>
    </div>
</template>